import React, { useEffect, useMemo, useState } from "react";
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel
} from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { MDBContainer } from "mdb-react-ui-kit";
import AdminService from "../api/AdminService";
import { useNavigate } from "react-router-dom";

const formatDateTime = (dateString) => {
  if (dateString) {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
  } else {
    return dateString;
  }
};

const AdminPanel = () => {
  const user = useSelector((state) => state.user.user);
  const subscriptions = useSelector((state) => state.user.subscriptions);

  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storageUsageMap, setStorageUsageMap] = useState({});
  const [storageUsageError, setStorageUsageError] = useState(null);

  // Filters and sorting state
  const [searchEmail, setSearchEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "", "active", "inactive"
  const [filterRole, setFilterRole] = useState(""); // "" or role value
  const [filterSubscription, setFilterSubscription] = useState(""); // "" = All, "none" = without subscription, or subscriptionId
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const openSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      loadData();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  const fetchStorageUsageForUsers = async (userIds) => {
    try {
      const usageData = await Promise.all(
        userIds.map(async (userId) => {
          const usage = await AdminService.getUserStorageUsage(userId);
          return { userId, usage };
        })
      );
      const usageMap = usageData.reduce((acc, { userId, usage }) => {
        acc[userId] = usage;
        return acc;
      }, {});
      setStorageUsageMap(usageMap);
      setStorageUsageError(null);
    } catch (err) {
      setStorageUsageError(err?.message || "Помилка отримання використаного місця.");
    }
  };

  const fetchUsers = async () => {
    try {
      const userList = await AdminService.getAllUsers();
      setUsers(userList);
      const userIds = userList.map((user) => user.id);
      await fetchStorageUsageForUsers(userIds);
      setError(null);
    } catch (_error) {
      setError("Не вдалося завантажити дані користувачів.");
    }
  };

  const getUserSubscription = (user) => {
    return subscriptions.find(sub => sub.id === user.subscriptionId)?.title || "Підписка відсутня";
  };

  const downloadFileArchive = async () => {
    try {
      const response = await AdminService.downloadFileArchive(user.id);
      if (response?.status === 200 && response.data) {
        // Створюємо blob URL для завантаження
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);

        // Створюємо тимчасове посилання для завантаження
        const link = document.createElement('a');
        link.href = url;
        link.download = `user_${user.id}_files_archive.zip`;
        document.body.appendChild(link);
        link.click();

        // Очищуємо ресурси
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        openSnackbar("Архів з файлами успішно завантажено.", "success");
      } else {
        openSnackbar("Не вдалося завантажити архів з файлами.", "warning");
      }
    } catch (e) {
      openSnackbar(e?.message || "Сталася помилка під час завантаження архіву з файлами.", "error");
    }
  };

  const cleanOrphanedFiles = async () => {
    try {
      const response = await AdminService.cleanOrphanedFiles();
      if (response?.status === 200) {
        openSnackbar("Непов’язані файли успішно видалено.", "success");
      } else {
        openSnackbar("Не вдалося видалити непов’язані файли.", "warning");
      }
    } catch (e) {
      openSnackbar(e?.message || "Сталася помилка під час видалення файлів.", "error");
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  // Unique roles from users for filter options
  const roleOptions = useMemo(() => {
    const set = new Set(users.map(u => u.role).filter(Boolean));
    return Array.from(set);
  }, [users]);

  // Subscription id to title map
  const subTitleById = useMemo(() => {
    const map = new Map();
    subscriptions.forEach(s => map.set(s.id, s.title));
    return map;
  }, [subscriptions]);

  // Filtering + sorting
  const displayedUsers = useMemo(() => {
    const term = searchEmail.trim().toLowerCase();
    let arr = users.filter(u => {
      if (term && !(u.email || "").toLowerCase().includes(term)) return false;
      if (filterStatus === "active" && !u.active) return false;
      if (filterStatus === "inactive" && u.active) return false;
      if (filterRole && u.role !== filterRole) return false;
      if (filterSubscription === "none" && (u.subscriptionId !== null && u.subscriptionId !== undefined)) return false;
      if (filterSubscription && filterSubscription !== "none" && String(u.subscriptionId) !== String(filterSubscription)) return false;
      return true;
    });

    const getValue = (u, key) => {
      switch (key) {
        case "id": return Number(u.id) || 0;
        case "email": return (u.email || "").toLowerCase();
        case "status": return u.active ? 1 : 0; // active first
        case "role": return (u.role || "").toLowerCase();
        case "subscription": return (subTitleById.get(u.subscriptionId) || (u.subscriptionId == null ? "(none)" : "")).toLowerCase();
        case "used": return storageUsageMap[u.id] ?? -1;
        case "total": return Number(u.totalStorageLimit) || 0;
        case "created": return dayjs(u.created).valueOf() || 0;
        case "lastLogin": return dayjs(u.lastLogin).valueOf() || 0;
        case "updated": return dayjs(u.updated).valueOf() || 0;
        default: return 0;
      }
    };

    const dir = sortOrder === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = getValue(a, sortBy);
      const vb = getValue(b, sortBy);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return arr;
  }, [users, searchEmail, filterStatus, filterRole, filterSubscription, sortBy, sortOrder, storageUsageMap, subTitleById]);

  const handleSort = (column) => {
    setSortBy((prevCol) => {
      if (prevCol === column) {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        return prevCol;
      }
      setSortOrder("asc");
      return column;
    });
  };

  return (
    <>
      <Box p={2} marginLeft={5} marginRight={5}>
        <MDBContainer className="d-flex justify-content-center align-items-center text-center mb-3">
          <Typography variant="h5" sx={{ flex: 1 }}>Адмін панель</Typography>
          <Button variant="contained" color="warning" onClick={cleanOrphanedFiles}>
            Видалити непов’язані файли з AWS
          </Button>
          <Button className="ms-5" variant="contained" color="success" onClick={downloadFileArchive}>
            Завантажити архів з файлами
          </Button>
        </MDBContainer>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              label="Пошук по email"
              placeholder="user@example.com"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="status-label">Статус</InputLabel>
              <Select
                labelId="status-label"
                label="Статус"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">Всі</MenuItem>
                <MenuItem value="active">Активний</MenuItem>
                <MenuItem value="inactive">Деактивований</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="role-label">Роль</InputLabel>
              <Select
                labelId="role-label"
                label="Роль"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="">Всі</MenuItem>
                {roleOptions.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="sub-label">Підписка</InputLabel>
              <Select
                labelId="sub-label"
                label="Підписка"
                value={filterSubscription}
                onChange={(e) => setFilterSubscription(e.target.value)}
              >
                <MenuItem value="">Всі</MenuItem>
                <MenuItem value="none">Без підписки</MenuItem>
                {subscriptions.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {error ? (
          <div
            className="container alert alert-warning text-center mt-3"
            role="alert"
          >
            {error}
          </div>
        ) : (
          loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
              <CircularProgress/>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxWidth: '90%' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={sortBy === 'id' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'id'}
                        direction={sortBy === 'id' ? sortOrder : 'asc'}
                        onClick={() => handleSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'email' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'email'}
                        direction={sortBy === 'email' ? sortOrder : 'asc'}
                        onClick={() => handleSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Статус
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'role' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'role'}
                        direction={sortBy === 'role' ? sortOrder : 'asc'}
                        onClick={() => handleSort('role')}
                      >
                        Роль
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'subscription' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'subscription'}
                        direction={sortBy === 'subscription' ? sortOrder : 'asc'}
                        onClick={() => handleSort('subscription')}
                      >
                        Підписка
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'used' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'used'}
                        direction={sortBy === 'used' ? sortOrder : 'asc'}
                        onClick={() => handleSort('used')}
                      >
                        Використано місця
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'total' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'total'}
                        direction={sortBy === 'total' ? sortOrder : 'asc'}
                        onClick={() => handleSort('total')}
                      >
                        Всього доступно місця
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'created' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'created'}
                        direction={sortBy === 'created' ? sortOrder : 'asc'}
                        onClick={() => handleSort('created')}
                      >
                        Створено
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'lastLogin' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'lastLogin'}
                        direction={sortBy === 'lastLogin' ? sortOrder : 'asc'}
                        onClick={() => handleSort('lastLogin')}
                      >
                        Останній логін
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'updated' ? sortOrder : false}>
                      <TableSortLabel
                        active={sortBy === 'updated'}
                        direction={sortBy === 'updated' ? sortOrder : 'asc'}
                        onClick={() => handleSort('updated')}
                      >
                        Оновлено
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.active ? "Активний" : "Деактивований"}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell>{getUserSubscription(user)}</TableCell>
                      <TableCell>
                        {storageUsageError
                          ? storageUsageError
                          : (storageUsageMap[user.id] !== undefined
                            ? `${storageUsageMap[user.id]} MB`
                            : "Завантаження...")}
                      </TableCell>
                      <TableCell>{user.totalStorageLimit} MB</TableCell>
                      <TableCell>{formatDateTime(user.created)}</TableCell>
                      <TableCell>{formatDateTime(user.lastLogin)}</TableCell>
                      <TableCell>{formatDateTime(user.updated)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ))}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity} elevation={6} variant="filled"
                  sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AdminPanel;
